import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  withDefault,
  type DecodedValueMap,
} from 'use-query-params';

import type { MarketplaceSearchParams } from '@ltvco/refresh-lib/v2';

/**
 * URL query-param config for the public marketplace search page. Mirrors the
 * `MarketplaceSearchParams` schema from `@ltvco/refresh-lib` (ported from the
 * Ember `app/utils/query-params.js`). The URL is the source of truth for
 * search state so results are shareable and back/forward friendly.
 */
export const marketplaceQueryConfig = {
  sellerType: withDefault(StringParam, ''),
  titleType: withDefault(StringParam, ''),
  zip: StringParam,
  radius: withDefault(NumberParam, 50),
  carType: withDefault(StringParam, ''),
  minYear: NumberParam,
  maxYear: NumberParam,
  makes: withDefault(ArrayParam, []),
  models: withDefault(ArrayParam, []),
  trims: withDefault(ArrayParam, []),
  drivetrains: withDefault(ArrayParam, []),
  transmissions: withDefault(ArrayParam, []),
  cylinders: withDefault(ArrayParam, []),
  fuelTypes: withDefault(ArrayParam, []),
  bodyTypes: withDefault(ArrayParam, []),
  bodySubtypes: withDefault(ArrayParam, []),
  doors: withDefault(ArrayParam, []),
  engines: withDefault(ArrayParam, []),
  engineSizes: withDefault(ArrayParam, []),
  engineBlocks: withDefault(ArrayParam, []),
  baseInteriorColors: withDefault(ArrayParam, []),
  baseExteriorColors: withDefault(ArrayParam, []),
  interiorColors: withDefault(ArrayParam, []),
  exteriorColors: withDefault(ArrayParam, []),
  oneOwner: StringParam,
  cleanTitle: StringParam,
  priceRange: withDefault(StringParam, '-'),
  milesRange: withDefault(StringParam, ''),
  domActiveRange: withDefault(StringParam, ''),
  msrpRange: withDefault(StringParam, ''),
  highwayMpgRange: withDefault(StringParam, ''),
  cityMpgRange: withDefault(StringParam, ''),
  page: withDefault(NumberParam, 1),
  sortBy: withDefault(StringParam, 'price'),
  sortOrder: withDefault(StringParam, 'asc'),
  nationwide: withDefault(BooleanParam, false),
};

export type MarketplaceQueryValues = DecodedValueMap<
  typeof marketplaceQueryConfig
>;

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/** Coerces the loosely-typed URL values into the lib's search-param shape. */
export function toSearchParams(
  values: MarketplaceQueryValues,
): MarketplaceSearchParams {
  return {
    sellerType: values.sellerType ?? '',
    titleType: values.titleType ?? '',
    zip: values.zip ?? null,
    radius: values.radius ?? 50,
    carType: values.carType ?? '',
    minYear: values.minYear ?? null,
    maxYear: values.maxYear ?? null,
    makes: toStringArray(values.makes),
    models: toStringArray(values.models),
    trims: toStringArray(values.trims),
    drivetrains: toStringArray(values.drivetrains),
    transmissions: toStringArray(values.transmissions),
    cylinders: toStringArray(values.cylinders),
    fuelTypes: toStringArray(values.fuelTypes),
    bodyTypes: toStringArray(values.bodyTypes),
    bodySubtypes: toStringArray(values.bodySubtypes),
    doors: toStringArray(values.doors),
    engines: toStringArray(values.engines),
    engineSizes: toStringArray(values.engineSizes),
    engineBlocks: toStringArray(values.engineBlocks),
    baseInteriorColors: toStringArray(values.baseInteriorColors),
    baseExteriorColors: toStringArray(values.baseExteriorColors),
    interiorColors: toStringArray(values.interiorColors),
    exteriorColors: toStringArray(values.exteriorColors),
    oneOwner: values.oneOwner ?? null,
    cleanTitle: values.cleanTitle ?? null,
    priceRange: values.priceRange ?? '-',
    milesRange: values.milesRange ?? '',
    domActiveRange: values.domActiveRange ?? '',
    msrpRange: values.msrpRange ?? '',
    highwayMpgRange: values.highwayMpgRange ?? '',
    cityMpgRange: values.cityMpgRange ?? '',
    page: values.page ?? 1,
    sortBy: values.sortBy ?? 'price',
    sortOrder: values.sortOrder ?? 'asc',
    nationwide: values.nationwide ?? false,
  };
}
